import { useMemo, useRef, useState } from "react";
import useClickOutside from "../../hooks/useClickOutside";
import ChipList from "../BuilderComponents/ChipList";
import MaterialIcon from "../BuilderComponents/MaterialIcon";
import Label from "./Label";

/**
 * A multi-select box component with search functionality.
 *
 * This component allows users to select multiple options from a dropdown list with search functionality. It supports both single and multiple label keys.
 *
 * @param {Array} options - Array of objects representing the selectable options.
 *                          Each object should have properties corresponding to the 'valueKey' and 'labelKey' values.
 * @param {string} valueKey - The key in the option objects that holds the value.
 * @param {Array} labelKey - The key(s) in the option objects that hold the label. Can be a single string or an array of strings.
 * @param {Array} selectedData - Array of initially selected data. Can be an array of objects or an array of strings (representing the valueKey).
 * @param {Function} onChange - Callback function to handle the selection change. Receives the array of selected options and the event as arguments.
 * @param {string} label - Optional label for the multi-select box.
 * @param {string} placeholder - Optional placeholder for the multi-select box.
 *
 * @example
 * <MultiSelectBox
 *   options={[
 *     { id: "1", name: "Item 1" },
 *     { id: "2", name: "Item 2" },
 *     { id: "3", name: "Item 3" }
 *   ]}
 *   valueKey="id"
 *   labelKey={["name"]}
 *   selectedData={[{ id: "2", name: "Item 2" }]} // Using an array of objects
 *   selectedData={["2"]} // Using an array of strings
 *   onChange={(selectedOptions, event) => console.log(selectedOptions)}
 *   label="Select Items"
 * />
 */

const MultiSelectBox = ({
  options,
  valueKey = "id",
  labelKey = ["name"],
  selectedData = [],
  onChange,
  label,
  placeholder = "select",
  containerClassName = "",
  labelClassName = ""
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const dropdownRef = useRef();
  useClickOutside(dropdownRef, () => {
    if (isDropdownOpen) setIsDropdownOpen(false);
  });

  // normalize selectedData into option objects
  const selectedOptions = useMemo(() => {
    if (!Array.isArray(selectedData)) return [];

    if (selectedData.length && typeof selectedData[0] === "object") {
      return selectedData;
    }

    return selectedData.map((item) => options.find((option) => option[valueKey] === item)).filter(Boolean); // filter out null/undefined
  }, [selectedData, options, valueKey]);

  const removeItem = (option, event) => {
    const newSelectedOptions = selectedOptions.filter((selected) => selected[valueKey] !== option[valueKey]);
    onChange(newSelectedOptions, event);
  };

  const handleAddItems = (option, event) => {
    if (!selectedOptions.some((sel) => sel[valueKey] === option[valueKey])) {
      const newSelectedOptions = [...selectedOptions, option];
      onChange(newSelectedOptions, event);
    }
  };

  const filteredOptions = useMemo(() => {
    return options
      .filter((option) => !selectedOptions.some((selected) => selected[valueKey] === option[valueKey]))
      .filter((option) => {
        const labels = Array.isArray(labelKey)
          ? labelKey.map((key) => option[key] || "").join(" ")
          : option[labelKey] || "";
        return labels.toLowerCase().includes(searchTerm.toLowerCase());
      });
  }, [options, selectedOptions, searchTerm, labelKey, valueKey]);

  const clearSelection = (e) => {
    e.stopPropagation();
    onChange([], e);
  };

  const containerClasses = `custom_select_box ${containerClassName}`.trim();

  return (
    <div className={containerClasses} ref={dropdownRef}>
      <MultiSelectInput
        selectedOptions={selectedOptions}
        searchTerm={searchTerm}
        onDropdownClick={() => setIsDropdownOpen(!isDropdownOpen)}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setIsDropdownOpen(true);
        }}
        onClearSelection={clearSelection}
        onRemoveItem={(option, e) => removeItem(option, e)}
        labelKey={labelKey}
        placeholder={placeholder}
        isDropdownOpen={isDropdownOpen}
        label={label}
        labelClassName={labelClassName}
      />
      {isDropdownOpen && (
        <DropdownOptions
          options={filteredOptions}
          labelKey={labelKey}
          onOptionClick={(option, e) => handleAddItems(option, e)}
        />
      )}
    </div>
  );
};

export default MultiSelectBox;

const MultiSelectInput = ({
  selectedOptions,
  searchTerm,
  onDropdownClick,
  onSearchChange,
  onClearSelection,
  onRemoveItem,
  labelKey,
  delimiter = ", ",
  placeholder,
  isDropdownOpen,
  label,
  labelClassName
}) => {
  return (
    <div className="form_element_wrapper_text">
      {label && <Label className={labelClassName}>{label}</Label>}
      <div className="selected_items" onClick={onDropdownClick}>
        <div className="item_wrapper">
          <ChipList
            chips={selectedOptions}
            labelKey={labelKey}
            delimiter={delimiter}
            removeChip={(removeItem, e) => onRemoveItem(removeItem, e)}
          />
          <input
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={onSearchChange}
            onClick={(e) => {
              e.stopPropagation();
              onDropdownClick();
            }}
          />
        </div>

        <div className="single_dropdown_arrow">
          {selectedOptions.length > 0 && (
            <MaterialIcon
              name="close"
              className="close_icon font_size_medium"
              style={{ color: "#667085" }}
              onClick={onClearSelection}
            />
          )}
          {isDropdownOpen ? (
            <MaterialIcon name="keyboard_arrow_down" style={{ color: "#667085" }} />
          ) : (
            <MaterialIcon name="keyboard_arrow_down" className="arrow_drop_right" style={{ color: "#667085" }} />
          )}
        </div>
      </div>
    </div>
  );
};

const DropdownOptions = ({ options, onOptionClick, labelKey, delimiter = ", " }) => {
  return (
    <div className="options_list">
      {options.length === 0 ? (
        <div className="option_item">No options</div>
      ) : (
        options.map((option, index) => (
          <div
            key={option.value + "-" + index}
            className="option_item"
            onClick={(e) => {
              e.stopPropagation();
              onOptionClick(option, e);
            }}
          >
            {getLabelsForSelectBox(option, labelKey, delimiter)}
          </div>
        ))
      )}
    </div>
  );
};

const getLabelsForSelectBox = (option, labelKey, delimiter) => {
  if (typeof option !== "object" || option === null) {
    return option;
  }

  if (Array.isArray(labelKey)) {
    return labelKey
      .map((key) => option[key])
      .filter(Boolean)
      .join(delimiter);
  }

  return option[labelKey];
};

export { DropdownOptions, getLabelsForSelectBox };
